import React from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  FormHelperText,
  InputAdornment,
} from '@material-ui/core';
import {
  DateTimePicker,
  DatePicker,
} from '@material-ui/pickers';
import {
  textType,
  numberType,
  dateType,
  dateTimeType,
  userType,
  phoneType,
  emailType,
  addressType,
  yesNoType,
} from '@pingum/api-services/EventDataService/EventDataTypes';

import AddressAutocomplete from '../../forms/AddressAutocomplete';
import UserDownshift from '../../forms/UserDownshift';
import {
  getYesNoRadioGroupValue,
  showYesNoMoreInfoField,
  getYesNoMoreInfoValue,
  getIconForEventData,
} from '../../../utils/eventDataHelper';
import styles from './styles';

const useStyles = makeStyles(styles);

export const EditEventDataFieldComponent = (props) => {
  const {
    eventDataField: data,
    onChange,
    disabled,
  } = props;
  const classes = useStyles();

  const handleEventDataChange = (e) => {
    const changedValue = e.target.value;
    onChange(changedValue);
  };

  const handleEventDataDateChange = (e) => {
    const changedValue = new Date(e._d); // eslint-disable-line no-underscore-dangle
    onChange(changedValue);
  };

  const handleEventDataUserChange = (user) => {
    let updatedUserData = data.value || '';
    const userInList = updatedUserData.indexOf(user) >= 0;
    if (userInList) {
      updatedUserData = updatedUserData.replace(user, '');
    } else {
      updatedUserData = updatedUserData.concat(`@[${user.userFullName}](${user.auth0UserId})`);
    }

    onChange(updatedUserData);
  };

  const descriptionSpan = !data.description
    ? <span />
    : <span dangerouslySetInnerHTML={{ __html: data.description.replace(/\n/g, '<br />').replace(/((http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*))/g, '<a href="$1">$1</a>') }} key={`description-part-${Math.random()}`} />;

  return (
    <div>
      {
        (
          data.type === textType
            || data.type === numberType
            || data.type === phoneType
            || data.type === emailType
        )
        && (
          <TextField
            required={data.required}
            error={data.error}
            helperText={<div><div>{data.helperText}</div>{data.description && <div>{descriptionSpan}</div>}</div>}
            FormHelperTextProps={{ component: 'div' }}
            fullWidth
            variant="outlined"
            type={data.type === numberType
              ? 'number'
              : 'text'}
            id={`data-fields-${data.name}`}
            label={data.name}
            value={data.value
              ? data.value
              : ''}
            onChange={handleEventDataChange}
            margin="normal"
            autoComplete="new-password"
            disabled={disabled}
            multiline={data.configuration.multilineTextField}
            rows={4}
            InputProps={{
              startAdornment: ((data.type === phoneType || data.type === emailType)
                ? (
                  <InputAdornment position="start">
                    {getIconForEventData(data, classes.eventDataIcon)}
                  </InputAdornment>
                )
                : <span />
              ),
            }}
          />
        )
      }
      {
        data.type === dateTimeType
          && (
            <div className={classes.datetimePickerContainer}>
              <DateTimePicker
                required={data.required}
                error={data.error}
                helperText={<div><div>{data.helperText}</div>{data.description && <div>{descriptionSpan}</div>}</div>}
                FormHelperTextProps={{ component: 'div' }}
                className={classes.datetimePicker}
                label={data.name}
                inputVariant="outlined"
                value={!data.value
                  ? null
                  : data.value}
                emptyLabel=""
                onChange={handleEventDataDateChange}
                disabled={disabled}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getIconForEventData(data, classes.eventDataIcon)}
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )
      }
      {
        data.type === dateType
          && (
            <div className={classes.datetimePickerContainer}>
              <DatePicker
                required={data.required}
                error={data.error}
                helperText={<div><div>{data.helperText}</div>{data.description && <div>{descriptionSpan}</div>}</div>}
                FormHelperTextProps={{ component: 'div' }}
                className={classes.datetimePicker}
                disableToolbar
                inputVariant="outlined"
                variant="inline"
                emptyLabel=""
                label={data.name}
                value={!data.value
                  ? null
                  : data.value}
                onChange={handleEventDataDateChange}
                disabled={disabled}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getIconForEventData(data, classes.eventDataIcon)}
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )
      }
      {
        data.type === userType
          && (
            <UserDownshift
              id={`data-fields-${data.name}`}
              fieldName={data.name}
              selectedItem={
                data.value
                  ? data.value.split(/(?=@\[)/)
                  : []
              }
              onChange={(user) => handleEventDataUserChange(user)}
              onRemoveItem={handleEventDataUserChange}
              itemToString={(user) => user.email}
              readonly={false}
              required={data.required}
              error={data.error}
              helperText={<div><div>{data.helperText}</div>{data.description && <div>{descriptionSpan}</div>}</div>}
            />
          )
      }
      {
        data.type === addressType
          && (
            <AddressAutocomplete
              label={data.name}
              required={data.required}
              error={data.error}
              helperText={<div><div>{data.helperText}</div>{data.description && <div>{descriptionSpan}</div>}</div>}
              value={data.value}
              onChange={handleEventDataChange}
            />
          )
      }
      {
        data.type === yesNoType
        && (
          <FormControl component="fieldset" margin="dense" required={data.required} size="small" error={data.error} className={classes.yesNoForm} fullWidth>
            <FormLabel component="label">{data.name}</FormLabel>
            <RadioGroup
              aria-label={data.name}
              name={data.name}
              value={getYesNoRadioGroupValue(data.value)}
              onChange={handleEventDataChange}
              row
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" size="small" />}
                label="Yes"
                labelPlacement="end"
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" size="small" />}
                label="No"
                labelPlacement="end"
              />
            </RadioGroup>
            { showYesNoMoreInfoField(data.value, data.configuration)
              && (
                <TextField
                  error={data.error}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  id={`more-info-yesno-${data.name}`}
                  label={data.configuration.moreInfoFieldLabel || `More information on "${data.name}"`}
                  value={data.value
                    ? getYesNoMoreInfoValue(data.value)
                    : ''}
                  onChange={(e) => { handleEventDataChange({ target: { value: `${getYesNoRadioGroupValue(data.value)}, ${e.target.value}` } }); }}
                  margin="dense"
                  disabled={disabled}
                />
              )}
            {(data.error || data.description)
              && (
                <FormHelperText component="div">
                  <div>
                    <div>{data.helperText}</div>
                    {data.description && <div>{descriptionSpan}</div>}
                  </div>
                </FormHelperText>
              )}
          </FormControl>
        )
      }
    </div>
  );
};

EditEventDataFieldComponent.propTypes = {
  eventDataField: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};
EditEventDataFieldComponent.defaultProps = {
  eventDataField: null,
  onChange: () => {},
  disabled: false,
};

export default EditEventDataFieldComponent;
